// src/components/typing/WordReplay.js
import React, { useState } from "react";
import "../../styles/WordReplay.css";

// ─── Main Component ───────────────────────────────────────────────
const WordReplay = ({ wordResults, mode }) => {
  const [showLegend, setShowLegend] = useState(true);

  if (!wordResults || wordResults.length === 0) return null;

  const correctCount  = wordResults.filter((w) => w.status === "correct" && !w.firstWrong).length;
  const retriedCount  = wordResults.filter((w) => w.status === "correct" && w.firstWrong).length;
  const wrongCount    = wordResults.filter((w) => w.status === "incorrect").length;
  const missedCount   = wordResults.filter((w) => w.status === "missed").length;
  const pendingCount  = wordResults.filter((w) => w.status === "pending").length;
  const extraCount    = wordResults.filter((w) => w.status === "extra").length;

  return (
    <div className="word-replay-card">

      {/* ── Header ── */}
      <div className="word-replay-header">
        <div className="word-replay-title-row">
          <h3 className="word-replay-title">
            {mode === "test"
              ? "📋 Test Result — Word by Word"
              : "📄 Practice Result — Word by Word"}
          </h3>
          <button
            className="word-replay-legend-toggle"
            onClick={() => setShowLegend((v) => !v)}
            aria-expanded={showLegend}
          >
            {showLegend ? "Hide Legend ▲" : "Show Legend ▼"}
          </button>
        </div>

        {/* ── Legend ── */}
        {showLegend && (
          <div className="word-replay-legend">
            <span className="wrl-item correct">
              <span className="wrl-dot" />
              Correct word
            </span>
            <span className="wrl-item retried">
              <span className="wrl-dot" />
              Wrong then corrected
            </span>
            <span className="wrl-item incorrect">
              <span className="wrl-dot" />
              Wrong word
            </span>
            <span className="wrl-item missed">
              <span className="wrl-dot" />
              Missed / Skipped
            </span>
            <span className="wrl-item pending">
              <span className="wrl-dot" />
              Not attempted
            </span>
            {extraCount > 0 && (
              <span className="wrl-item extra">
                <span className="wrl-dot" />
                Extra word
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Quick Count Bar ── */}
      <div className="word-replay-count-bar">
        {correctCount > 0 && (
          <span className="wrcb-item correct">✅ {correctCount} correct</span>
        )}
        {retriedCount > 0 && (
          <span className="wrcb-item retried">🔄 {retriedCount} corrected after retry</span>
        )}
        {wrongCount > 0 && (
          <span className="wrcb-item incorrect">❌ {wrongCount} wrong</span>
        )}
        {missedCount > 0 && (
          <span className="wrcb-item missed">🟡 {missedCount} missed</span>
        )}
        {pendingCount > 0 && (
          <span className="wrcb-item pending">⏳ {pendingCount} not attempted</span>
        )}
        {extraCount > 0 && (
          <span className="wrcb-item extra">➕ {extraCount} extra</span>
        )}
      </div>

      {/* ── Word Chips Body ── */}
      <div className="word-replay-body">
        {wordResults.map((result, index) => (
          <WordChip key={index} result={result} />
        ))}
      </div>

    </div>
  );
};

// ─── WordChip ─────────────────────────────────────────────────────
const WordChip = ({ result }) => {
  const { original, status, typedWord, firstWrong } = result;

  // ── Correct with retry history ──
  // Status is correct but firstWrong exists → show "proc → process"
  if (status === "correct" && firstWrong) {
    return (
      <span
        className="word-chip-group retried-group"
        title={`First typed "${firstWrong}", then corrected to "${original}"`}
      >
        {/* Wrong first attempt — red strikethrough */}
        <span className="word-chip incorrect">
          {firstWrong}
        </span>
        {/* Arrow */}
        <span className="word-chip-arrow" aria-hidden="true">→</span>
        {/* Final correct word — green */}
        <span className="word-chip correct">
          {original}
        </span>
      </span>
    );
  }

  // ── Correct (clean, no retry) ──
  if (status === "correct") {
    return (
      <span
        className="word-chip correct"
        title="Correct"
        aria-label={`Correct: ${original}`}
      >
        {original}
      </span>
    );
  }

  // ── Incorrect (typed wrong, never corrected) ──
  if (status === "incorrect") {
    return (
      <span
        className="word-chip-group incorrect-group"
        title={`You typed "${typedWord}" — correct is "${original}"`}
      >
        <span className="word-chip incorrect">
          {typedWord || "?"}
        </span>
        <span className="word-chip-arrow" aria-hidden="true">→</span>
        <span className="word-chip correct-hint">
          {original}
        </span>
      </span>
    );
  }

  // ── Missed ──
  if (status === "missed") {
    return (
      <span
        className="word-chip missed"
        title="This word was skipped"
        aria-label={`Missed: ${original}`}
      >
        {original}
      </span>
    );
  }

  // ── Pending ──
  if (status === "pending") {
    return (
      <span
        className="word-chip pending"
        title="Not attempted — time ran out"
        aria-label={`Not attempted: ${original}`}
      >
        {original}
      </span>
    );
  }

  // ── Extra ──
  if (status === "extra") {
    return (
      <span
        className="word-chip extra"
        title="Extra word — not in original text"
        aria-label={`Extra word: ${typedWord}`}
      >
        +{typedWord}
      </span>
    );
  }

  return null;
};

export default WordReplay;